import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Save, X, Search, Wrench } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { stationLabel, opStation, autoTonnageForProcess, STATION_TONNAGES } from "@/utils/processStation";

const MACHINE_TYPES = [
  { value: "press", label: "Press" },
  { value: "cnc", label: "CNC" },
  { value: "vmc", label: "VMC" },
  { value: "sliding_head", label: "Sliding Head" },
  { value: "welding", label: "Welding" },
  { value: "other", label: "Other" },
];
const MACHINE_TYPE_LABEL = Object.fromEntries(MACHINE_TYPES.map((m) => [m.value, m.label]));

function emptyForm() {
  return { name: "", default_cost_mode: "per_piece", default_rate: 0, default_cycle_time_sec: 0, machine_op: false, machine_type: "", machine_types: [], no_machine: false, default_tonnage: "", notes: "" };
}

export default function Operations() {
  const { user, hasPermission } = useAuth();
  // Gate on the permission (not the role) so the Roles & Permissions checkboxes actually apply.
  const canCreateOrEdit = !!hasPermission("edit_process_master");
  const canDelete = !!hasPermission("edit_process_master");
  const [ops, setOps] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // User-added machine types + no-machine stations (persist locally, merged with built-ins).
  const ls = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || d); } catch { return JSON.parse(d); } };
  const [customMach, setCustomMach] = useState(() => ls("op_custom_machine_types", "[]"));
  const [customStations, setCustomStations] = useState(() => ls("op_custom_stations", "[]"));
  const [hiddenMach, setHiddenMach] = useState(() => ls("op_hidden_machine_types", "[]"));   // hidden built-ins
  const [hiddenStations, setHiddenStations] = useState(() => ls("op_hidden_stations", "[]"));
  useEffect(() => { try { localStorage.setItem("op_custom_machine_types", JSON.stringify(customMach)); } catch { /* ignore */ } }, [customMach]);
  useEffect(() => { try { localStorage.setItem("op_custom_stations", JSON.stringify(customStations)); } catch { /* ignore */ } }, [customStations]);
  useEffect(() => { try { localStorage.setItem("op_hidden_machine_types", JSON.stringify(hiddenMach)); } catch { /* ignore */ } }, [hiddenMach]);
  useEffect(() => { try { localStorage.setItem("op_hidden_stations", JSON.stringify(hiddenStations)); } catch { /* ignore */ } }, [hiddenStations]);
  const allMachineTypes = [...MACHINE_TYPES.filter((m) => !hiddenMach.includes(m.value)), ...customMach];
  const allStations = [...STATION_TONNAGES.filter((s) => !hiddenStations.includes(s)), ...customStations];
  const isCustomMach = (v) => customMach.some((m) => m.value === v);
  const isCustomStation = (s) => customStations.includes(s);

  function addMachineType() {
    const name = (window.prompt("New machine type name:") || "").trim();
    if (!name) return;
    const value = "x_" + name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") + "_" + customMach.length;
    setCustomMach((c) => [...c, { value, label: name }]);
  }
  function editMachineType(mt) {
    const name = (window.prompt("Rename machine type:", mt.label) || "").trim();
    if (!name) return;
    setCustomMach((c) => c.map((m) => m.value === mt.value ? { ...m, label: name } : m));
  }
  function delMachineType(mt) {
    if (isCustomMach(mt.value)) setCustomMach((c) => c.filter((m) => m.value !== mt.value));
    else setHiddenMach((h) => [...h, mt.value]);   // hide a built-in
    setEditing((e) => e ? ({ ...e, machine_type: e.machine_type === mt.value ? "" : e.machine_type,
                             machine_types: (e.machine_types || []).filter((v) => v !== mt.value) }) : e);
  }
  function addStation() {
    const name = (window.prompt("New station / manpower option:") || "").trim();
    if (!name || allStations.includes(name)) return;
    setCustomStations((c) => [...c, name]);
  }
  function editStation(s) {
    const name = (window.prompt("Rename station:", s) || "").trim();
    if (!name || (name !== s && allStations.includes(name))) return;
    setCustomStations((c) => c.map((x) => x === s ? name : x));
    if (editing?.default_tonnage === s) setEditing((e) => ({ ...e, default_tonnage: name }));
  }
  function delStation(name) {
    if (isCustomStation(name)) setCustomStations((c) => c.filter((s) => s !== name));
    else setHiddenStations((h) => [...h, name]);   // hide a built-in
    if (editing?.default_tonnage === name) setEditing((e) => ({ ...e, default_tonnage: "" }));
  }

  async function load() {
    setBusy(true);
    try {
      const r = await api.get("/operations");
      setOps(r.data || []);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = ops.filter((o) =>
    !q ||
    o.name.toLowerCase().includes(q.toLowerCase()) ||
    (o.notes || "").toLowerCase().includes(q.toLowerCase())
  );

  function startNew() { setEditing({ ...emptyForm(), _isNew: true }); setError(""); }
  function startEdit(o) { setEditing({ ...o, machine_types: o.machine_types || (o.machine_type ? [o.machine_type] : []) }); setError(""); }

  async function save() {
    if (!editing.name?.trim()) { setError("Name is required"); return; }
    setBusy(true); setError("");
    try {
      const body = {
        name: editing.name.trim(),
        default_cost_mode: editing.default_cost_mode,
        default_rate: Number(editing.default_rate) || 0,
        default_cycle_time_sec: Number(editing.default_cycle_time_sec) || 0,
        machine_op: !editing.no_machine && !!editing.machine_op,
        machine_types: (!editing.no_machine && editing.machine_op) ? (editing.machine_types || []) : [],
        machine_type: (!editing.no_machine && editing.machine_op) ? ((editing.machine_types || [])[0] || "") : "",
        no_machine: !!editing.no_machine,
        default_tonnage: editing.default_tonnage || "",   // keep the station for no-machine (Manpower etc.) too
        notes: editing.notes || "",
      };
      if (editing._isNew) await api.post("/operations", body);
      else await api.put(`/operations/${editing.operation_id}`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function del(o) {
    if (!window.confirm(`Delete operation "${o.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/operations/${o.operation_id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="operations-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wrench size={22} className="text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Operations <span className="text-base font-medium text-slate-400">({filtered.length})</span></h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Catalog of sheet-metal operations. Engineers pick from this when adding process steps on NPDs.
            </p>
          </div>
        </div>
        {canCreateOrEdit && (
          <button onClick={startNew} data-testid="op-new-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold">
            <Plus size={14} /> New Operation
          </button>
        )}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          placeholder="Search by name or notes..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md w-full"
          data-testid="op-search"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="operations-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium w-10">#</th>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Default Mode</th>
              <th className="text-right px-4 py-2 font-medium">Default Rate</th>
              <th className="text-right px-4 py-2 font-medium">Cycle (s)</th>
              <th className="text-center px-4 py-2 font-medium">Machine op</th>
              <th className="text-center px-4 py-2 font-medium" title="Auto-selected tonnage/station on the costing sheet">Auto tonnage</th>
              <th className="text-left px-4 py-2 font-medium">Notes</th>
              <th className="text-right px-4 py-2 font-medium w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-slate-400 text-xs">No operations found.</td></tr>
            )}
            {filtered.map((o, idx) => (
              <tr key={o.operation_id} className="hover:bg-slate-50" data-testid={`op-row-${o.operation_id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{idx + 1}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{o.name}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`inline-block px-1.5 py-0.5 rounded ${o.default_cost_mode === "per_kg" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>
                    {o.default_cost_mode === "per_kg" ? "₹/kg" : "₹/pc"}
                  </span>
                </td>
                <td className="px-4 py-2 text-right text-xs font-mono">{Number(o.default_rate || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-xs font-mono">{Number(o.default_cycle_time_sec || 0) > 0 ? Number(o.default_cycle_time_sec).toFixed(1) : "—"}</td>
                <td className="px-4 py-2 text-center text-xs">
                  {o.machine_op
                    ? (() => {
                        const types = (o.machine_types && o.machine_types.length) ? o.machine_types : (o.machine_type ? [o.machine_type] : []);
                        const labelOf = (v) => MACHINE_TYPE_LABEL[v] || allMachineTypes.find((m) => m.value === v)?.label || v;
                        return types.length
                          ? <span className="inline-flex flex-wrap justify-center gap-1">{types.map((v) => <span key={v} className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">{labelOf(v)}</span>)}</span>
                          : <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">Machine</span>;
                      })()
                    : o.no_machine
                    ? <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">No machine</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2 text-center text-xs">
                  {(() => {
                    const s = opStation(o);
                    if (s.kind === "station") return <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold" title={s.explicit ? "Set manually on this operation" : "Auto-filled on the costing sheet"}>{s.label}{s.explicit ? " •" : ""}</span>;
                    if (s.kind === "no-machine") return <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium" title="No tonnage — excluded from Machine Requirement">{s.label}</span>;
                    return <span className="text-slate-300" title="Press step — engineer picks the tonnage">—</span>;
                  })()}
                </td>
                <td className="px-4 py-2 text-xs text-slate-600">{o.notes || "—"}</td>
                <td className="px-4 py-2 text-right">
                  {canCreateOrEdit && (
                    <button onClick={() => startEdit(o)} className="text-sky-600 hover:text-sky-700 p-1 mr-1" title="Edit" data-testid={`op-edit-${o.operation_id}`}>
                      <Edit size={14} />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => del(o)} className="text-slate-400 hover:text-red-600 p-1" title="Delete" data-testid={`op-delete-${o.operation_id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="op-editor">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800">{editing._isNew ? "New Operation" : "Edit Operation"}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            {error && <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
            <div className="mb-3">
              <label className="block text-xs text-slate-500 mb-1">Name *</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                     className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" autoFocus data-testid="op-name-input" />
              {(() => {
                const s = opStation(editing);
                return (
                  <div className="mt-1.5 text-[11px] flex items-center gap-1.5" data-testid="op-auto-tonnage">
                    <span className="text-slate-500">Auto tonnage on costing:</span>
                    {s.kind === "station"
                      ? <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">{s.label}</span>
                      : s.kind === "no-machine"
                      ? <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">No machine</span>
                      : <span className="text-slate-400">— (press step — engineer picks the tonnage)</span>}
                    {s.explicit && <span className="text-slate-400">(set manually below)</span>}
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Default Mode</label>
                <select value={editing.default_cost_mode} onChange={(e) => setEditing({ ...editing, default_cost_mode: e.target.value })}
                        className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="op-mode-input">
                  <option value="per_piece">₹ per piece</option>
                  <option value="per_kg">₹ per kg</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Default Rate</label>
                <input type="number" step="0.01" value={editing.default_rate}
                       onChange={(e) => setEditing({ ...editing, default_rate: e.target.value })}
                       className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="op-rate-input" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Default Cycle Time (sec/pc)</label>
                <input type="number" step="0.1" min="0" value={editing.default_cycle_time_sec ?? 0}
                       onChange={(e) => setEditing({ ...editing, default_cycle_time_sec: e.target.value })}
                       className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm" data-testid="op-cycle-input" />
                <div className="text-[11px] text-slate-500 mt-1">Auto-fills the costing when this operation is picked.</div>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer pb-1.5">
                  <input type="checkbox" checked={!!editing.machine_op} disabled={!!editing.no_machine}
                         onChange={(e) => setEditing({ ...editing, machine_op: e.target.checked, machine_type: e.target.checked ? editing.machine_type : "" })}
                         className="h-4 w-4 accent-orange-600 disabled:opacity-40" data-testid="op-machine-input" />
                  Machine operation — cycle time required
                </label>
              </div>
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={!!editing.no_machine}
                       onChange={(e) => setEditing({ ...editing, no_machine: e.target.checked, machine_op: e.target.checked ? false : editing.machine_op, machine_type: e.target.checked ? "" : editing.machine_type })}
                       className="h-4 w-4 accent-orange-600" data-testid="op-nomachine-input" />
                No machine — e.g. Shearing (no tonnage, excluded from Machine Requirement)
              </label>
            </div>
            {/* No machine → pick a Station / Manpower option (tick boxes, editable). */}
            {editing.no_machine && (
              <div className="mb-4">
                <label className="block text-xs text-slate-500 mb-1.5">Station / Manpower <span className="text-slate-400">(auto-fills on the costing)</span></label>
                <div className="flex flex-wrap gap-2">
                  {allStations.map((s) => (
                    <span key={s} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded text-sm ${editing.default_tonnage === s ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={editing.default_tonnage === s}
                               onChange={() => setEditing({ ...editing, default_tonnage: editing.default_tonnage === s ? "" : s })}
                               className="h-4 w-4 accent-emerald-600" />
                        {s}
                      </label>
                      {isCustomStation(s) && <button onClick={() => editStation(s)} className="text-slate-300 hover:text-slate-700" title="Rename"><Edit size={11} /></button>}
                      <button onClick={() => delStation(s)} className="text-slate-300 hover:text-red-600" title="Remove"><X size={12} /></button>
                    </span>
                  ))}
                  <button onClick={addStation} className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 rounded text-sm text-slate-500 hover:bg-slate-50"><Plus size={13} /> Add</button>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Choose Manpower / Deburring / Ultrasonic (or add your own) for this no-machine operation.</div>
              </div>
            )}
            {/* Machine op → default tonnage/station dropdown (auto-derive or pin). */}
            {!editing.no_machine && (
              <div className="mb-4">
                <label className="block text-xs text-slate-500 mb-1">Default tonnage / station <span className="text-slate-400">(auto-fills on the costing)</span></label>
                <select value={editing.default_tonnage || ""}
                        onChange={(e) => setEditing({ ...editing, default_tonnage: e.target.value })}
                        className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm bg-white" data-testid="op-tonnage-input">
                  <option value="">Auto (from name){autoTonnageForProcess(editing.name) ? ` → ${autoTonnageForProcess(editing.name)}` : " — press step, engineer picks"}</option>
                  {allStations.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">Pin a station for this operation, or leave on Auto to derive it from the name.</div>
              </div>
            )}
            {editing.machine_op && (
              <div className="mb-4">
                <label className="block text-xs text-slate-500 mb-1.5">Machine type <span className="text-slate-400">(tick one or more)</span></label>
                <div className="flex flex-wrap gap-2">
                  {allMachineTypes.map((mt) => {
                    const sel = (editing.machine_types || []).includes(mt.value);
                    return (
                    <span key={mt.value}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded text-sm ${sel ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                      data-testid={`op-machtype-${mt.value}`}>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={sel}
                               onChange={() => setEditing((e) => {
                                 const cur = e.machine_types || [];
                                 const next = cur.includes(mt.value) ? cur.filter((v) => v !== mt.value) : [...cur, mt.value];
                                 return { ...e, machine_types: next, machine_type: next[0] || "" };
                               })}
                               className="h-4 w-4 accent-orange-600" />
                        {mt.label}
                      </label>
                      {isCustomMach(mt.value) && <button onClick={() => editMachineType(mt)} className="text-slate-300 hover:text-slate-700" title="Rename"><Edit size={11} /></button>}
                      <button onClick={() => delMachineType(mt)} className="text-slate-300 hover:text-red-600" title="Remove"><X size={12} /></button>
                    </span>
                  );})}
                  <button onClick={addMachineType} className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-slate-300 rounded text-sm text-slate-500 hover:bg-slate-50"><Plus size={13} /> Add machine</button>
                </div>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-xs text-slate-500 mb-1">Notes</label>
              <input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                     className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm"
                     placeholder="e.g. 'Cut blank from coil'" data-testid="op-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button onClick={() => setEditing(null)} disabled={busy} className="px-3 py-1.5 text-sm border border-slate-300 rounded">Cancel</button>
              <button onClick={save} disabled={busy || !editing.name?.trim()} data-testid="op-save-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
